import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  RefreshCw,
  Plus,
  Ticket,
  Search,
  Clock,
  User,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Send,
  UserPlus,
  Eye,
  ArrowRight,
  Inbox,
  Timer,
  TrendingUp,
  AlertTriangle,
  Building2,
  Tag,
  FileText,
  Zap,
  X,
  Archive,
  Bug,
  Users
} from 'lucide-react';
import { SortableTableHead, sortData } from '../ui/sortable-table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SLA Configuration (in hours)
const SLA_CONFIG = {
  urgent: { firstResponse: 1, resolution: 4 },
  high: { firstResponse: 4, resolution: 24 },
  medium: { firstResponse: 8, resolution: 48 },
  low: { firstResponse: 24, resolution: 72 }
};

// Status configurations
const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400', icon: Inbox },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400', icon: Timer },
  waiting_customer: { label: 'Waiting', color: 'bg-purple-500/20 text-purple-400', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
  closed: { label: 'Closed', color: 'bg-muted0/20 text-muted-foreground', icon: XCircle }
};

// Priority configurations
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-muted0/20 text-muted-foreground' },
  medium: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
};

// Category configurations
const CATEGORY_CONFIG = {
  billing: { label: 'Billing', color: 'text-emerald-400' },
  technical: { label: 'Technical', color: 'text-blue-400' },
  account: { label: 'Account', color: 'text-violet-400' },
  feature_request: { label: 'Feature Request', color: 'text-cyan-400' },
  bug_report: { label: 'Bug Report', color: 'text-red-400' },
  other: { label: 'Other', color: 'text-muted-foreground' }
};

// Check if ticket is breaching SLA
const getSlaStatus = (ticket) => {
  if (!ticket || ticket.status === 'resolved' || ticket.status === 'closed') {
    return null;
  }
  
  const now = new Date();
  const created = new Date(ticket.created_at);
  const hoursOpen = (now - created) / (1000 * 60 * 60);
  const sla = SLA_CONFIG[ticket.priority] || SLA_CONFIG.medium;
  
  // Check first response SLA
  if (!ticket.first_response_at) {
    if (hoursOpen > sla.firstResponse) {
      return { type: 'breached', message: 'First response SLA breached' };
    }
    if (hoursOpen > sla.firstResponse * 0.75) {
      return { type: 'warning', message: 'First response SLA at risk' };
    }
  }
  
  // Check resolution SLA
  if (hoursOpen > sla.resolution) {
    return { type: 'breached', message: 'Resolution SLA breached' };
  }
  if (hoursOpen > sla.resolution * 0.75) {
    return { type: 'warning', message: 'Resolution SLA at risk' };
  }
  
  return null;
};

export default function OwnerAdminSupport() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedTickets, setSelectedTickets] = useState([]);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, ticket: null });
  const [assignDialog, setAssignDialog] = useState({ open: false, ticket: null });
  const [templatesDialog, setTemplatesDialog] = useState(false);
  
  // Form states
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('other');
  const [newPriority, setNewPriority] = useState('medium');
  const [newComment, setNewComment] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let ticketUrl = `${API_URL}/api/owner-admin/tickets?`;
      if (search) ticketUrl += `search=${encodeURIComponent(search)}&`;
      if (statusFilter !== 'all') ticketUrl += `status=${statusFilter}&`;
      if (priorityFilter !== 'all') ticketUrl += `priority=${priorityFilter}&`;
      
      const [ticketsRes, statsRes, staffRes, templatesRes, summaryRes, errorsRes] = await Promise.all([
        fetch(ticketUrl, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/tickets/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/staff`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/tickets/templates`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/support-console/summary`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/client-errors?resolved=false&limit=50`, { credentials: 'include' })
      ]);
      
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || []);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (staffRes.ok) {
        const data = await staffRes.json();
        setStaff(data.staff || []);
      }
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }

      if (errorsRes.ok) {
        const data = await errorsRes.json();
        setClientErrors(data.errors || data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, priorityFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setViewDialog({ open: true, ticket: data });
      }
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleCreate = async () => {
    if (!newSubject.trim() || !newDescription.trim()) {
      toast.error('Please fill in subject and description');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newSubject,
          description: newDescription,
          category: newCategory,
          priority: newPriority
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Ticket ${data.ticket_id} created`);
        setCreateDialog(false);
        setNewSubject('');
        setNewDescription('');
        setNewCategory('other');
        setNewPriority('medium');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Status updated');
        fetchData();
        if (viewDialog.ticket?.ticket_id === ticketId) {
          fetchTicketDetails(ticketId);
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async () => {
    if (!assignDialog.ticket) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets/${assignDialog.ticket.ticket_id}`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: selectedAssignee || null })
      });
      
      if (response.ok) {
        toast.success('Ticket assigned');
        setAssignDialog({ open: false, ticket: null });
        setSelectedAssignee('');
        fetchData();
      } else {
        toast.error('Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignToMe = async (ticketId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}/assign-me`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Ticket assigned to you');
        fetchData();
      } else {
        toast.error('Failed to assign ticket');
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !viewDialog.ticket) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/tickets/${viewDialog.ticket.ticket_id}/comments`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newComment,
          internal: isInternalNote
        })
      });
      
      if (response.ok) {
        toast.success('Comment added');
        setNewComment('');
        setIsInternalNote(false);
        fetchTicketDetails(viewDialog.ticket.ticket_id);
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (date) => {
    if (!date) return '-';
    const now = new Date();
    const then = new Date(date);
    const diff = Math.floor((now - then) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const handleConvertError = async (errorId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/client-errors/${errorId}/convert-to-ticket`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.already_existed ? `Already linked to ${data.ticket_id}` : `Ticket ${data.ticket_id} created`);
        fetchData();
      } else {
        toast.error('Failed to convert error');
      }
    } catch (error) {
      toast.error('Failed to convert error');
    }
  };

  const handleResolveError = async (errorId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/client-errors/${errorId}/resolve`, {
        credentials: 'include',
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Error resolved');
        fetchData();
      } else {
        toast.error('Failed to resolve error');
      }
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  // Bulk selection handlers
  const meId = summary?.me?.admin_id;
  const viewFiltered = useMemo(() => {
    if (activeTab === 'mine') return tickets.filter(t => t.assigned_to === meId);
    if (activeTab === 'unassigned') return tickets.filter(t => !t.assigned_to);
    return tickets;
  }, [tickets, activeTab, meId]);
  const sortedTickets = useMemo(() => sortData(viewFiltered, sort), [viewFiltered, sort]);
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedTickets(sortedTickets.map(t => t.ticket_id));
    } else {
      setSelectedTickets([]);
    }
  };

  const handleSelectTicket = (ticketId, checked) => {
    if (checked) {
      setSelectedTickets(prev => [...prev, ticketId]);
    } else {
      setSelectedTickets(prev => prev.filter(id => id !== ticketId));
    }
  };

  const isAllSelected = sortedTickets.length > 0 && selectedTickets.length === sortedTickets.length;

  // Bulk actions
  const handleBulkClose = async () => {
    if (selectedTickets.length === 0) return;
    
    setSubmitting(true);
    try {
      const promises = selectedTickets.map(ticketId => 
        fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}/status`, {
          credentials: 'include',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'closed' })
        })
      );
      
      await Promise.all(promises);
      toast.success(`Closed ${selectedTickets.length} ticket(s)`);
      setSelectedTickets([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to close tickets');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkResolve = async () => {
    if (selectedTickets.length === 0) return;
    
    setSubmitting(true);
    try {
      const promises = selectedTickets.map(ticketId => 
        fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}/status`, {
          credentials: 'include',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'resolved' })
        })
      );
      
      await Promise.all(promises);
      toast.success(`Resolved ${selectedTickets.length} ticket(s)`);
      setSelectedTickets([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve tickets');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSetPriority = async (priority) => {
    if (selectedTickets.length === 0) return;
    
    setSubmitting(true);
    try {
      const promises = selectedTickets.map(ticketId => 
        fetch(`${API_URL}/api/owner-admin/tickets/${ticketId}`, {
          credentials: 'include',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ priority })
        })
      );
      
      await Promise.all(promises);
      toast.success(`Updated priority for ${selectedTickets.length} ticket(s)`);
      setSelectedTickets([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update priority');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !tickets.length) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Support Console</h1>
          <p className="text-muted-foreground">Tickets, client errors, and team workload — one inbox</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/owner-admin/staff')}
            className="border-white/20 text-muted-foreground/80"
            data-testid="support-manage-staff-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            Manage Staff
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setTemplatesDialog(true)} 
            className="border-white/20 text-muted-foreground/80"
          >
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </Button>
          <Button variant="outline" onClick={fetchData} className="border-white/20 text-muted-foreground/80">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Queue pills */}
      <div className="flex flex-wrap items-center gap-2" data-testid="support-queue-pills">
        {[
          { key: 'all', label: 'All tickets', count: tickets.length },
          { key: 'mine', label: 'My queue', count: summary?.my_queue ?? 0 },
          { key: 'unassigned', label: 'Unassigned', count: summary?.unassigned ?? 0 },
          { key: 'errors', label: 'Client errors', count: summary?.unresolved_errors ?? 0, icon: Bug },
        ].map((pill) => (
          <button
            key={pill.key}
            onClick={() => { setActiveTab(pill.key); setSelectedTickets([]); }}
            data-testid={`support-queue-${pill.key}`}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm border transition-colors ${
              activeTab === pill.key
                ? 'bg-emerald-600 border-emerald-500 text-white'
                : 'bg-white/[0.06] border-white/15 text-muted-foreground hover:bg-white/[0.12]'
            }`}
          >
            {pill.icon && <pill.icon className="h-3.5 w-3.5" />}
            {pill.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeTab === pill.key ? 'bg-white/20' : 'bg-white/10'}`}>
              {pill.count}
            </span>
          </button>
        ))}
      </div>

      {/* Team workload */}
      {summary?.workload?.length > 0 && (
        <Card className="bg-card border-white/15" data-testid="support-team-workload">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-emerald-400" />
                Team workload
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/owner-admin/staff')}
                className="text-emerald-400 hover:text-emerald-300 h-7"
                data-testid="support-add-staff-link"
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                Add support staff
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.workload.map((s) => (
                <div
                  key={s.admin_id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10"
                  data-testid={`workload-${s.admin_id}`}
                >
                  <div className="h-6 w-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs text-emerald-400">
                    {(s.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="leading-tight">
                    <p className="text-xs text-white">{s.name}</p>
                    <p className="text-[10px] text-muted-foreground">{(s.role || '').replace(/_/g, ' ')}</p>
                  </div>
                  <Badge className={`ml-1 ${s.open_tickets > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-white/10 text-muted-foreground'}`}>
                    {s.open_tickets}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SLA Alert Banner - show if there are breached tickets */}
      {tickets.some(t => getSlaStatus(t)?.type === 'breached') && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-red-400" />
            <div>
              <p className="text-red-200 font-medium">SLA Breach Alert</p>
              <p className="text-sm text-red-300/80">
                {tickets.filter(t => getSlaStatus(t)?.type === 'breached').length} ticket(s) have breached SLA. Immediate attention required.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedTickets.length > 0 && (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {selectedTickets.length} selected
                </Badge>
                <span className="text-muted-foreground text-sm">
                  ticket(s) selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-white/20 text-muted-foreground/80"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Set Priority
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleBulkSetPriority('urgent')}>
                      <span className="text-red-400">Urgent</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkSetPriority('high')}>
                      <span className="text-orange-400">High</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkSetPriority('medium')}>
                      <span className="text-blue-400">Medium</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkSetPriority('low')}>
                      <span className="text-muted-foreground">Low</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkResolve}
                  disabled={submitting}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Resolve
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkClose}
                  disabled={submitting}
                  className="border-white/20 text-muted-foreground/80"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Close
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedTickets([])}
                  className="text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Tickets</p>
                <p className="text-2xl font-bold text-blue-400">{stats?.by_status?.open || 0}</p>
              </div>
              <Inbox className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-yellow-400">{stats?.by_status?.in_progress || 0}</p>
              </div>
              <Timer className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Urgent/High</p>
                <p className="text-2xl font-bold text-red-400">
                  {(stats?.urgent_count || 0) + (stats?.high_priority_count || 0)}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved (7d)</p>
                <p className="text-2xl font-bold text-emerald-400">{stats?.resolved_7d || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client errors view */}
      {activeTab === 'errors' && (
        <Card className="bg-card border-white/15" data-testid="support-errors-table">
          <CardContent className="p-0">
            {clientErrors.length === 0 ? (
              <div className="text-center py-12">
                <Bug className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No unresolved client errors 🎉</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/15 hover:bg-white/[0.07]">
                    <TableHead className="text-muted-foreground">Error</TableHead>
                    <TableHead className="text-muted-foreground">User</TableHead>
                    <TableHead className="text-muted-foreground">Count</TableHead>
                    <TableHead className="text-muted-foreground">Last seen</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientErrors.map((err) => (
                    <TableRow key={err.error_id} className="border-white/15 hover:bg-white/[0.07]" data-testid={`error-row-${err.error_id}`}>
                      <TableCell className="max-w-md">
                        <p className="text-sm text-white truncate" title={err.message}>{err.message}</p>
                        <p className="text-xs text-muted-foreground truncate">{err.url || '-'}</p>
                        {err.ticket_id && (
                          <Badge className="mt-1 bg-emerald-500/20 text-emerald-400 text-xs">
                            <Ticket className="h-3 w-3 mr-1" />{err.ticket_id}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{err.user_email || '-'}</TableCell>
                      <TableCell>
                        <Badge className={err.count > 5 ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-muted-foreground'}>
                          {err.count || 1}×
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatTimeAgo(err.last_seen_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!err.ticket_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConvertError(err.error_id)}
                              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-7 text-xs"
                              data-testid={`convert-error-${err.error_id}`}
                            >
                              <Ticket className="h-3 w-3 mr-1" />
                              Convert to ticket
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResolveError(err.error_id)}
                            className="text-muted-foreground h-7 text-xs"
                            data-testid={`resolve-error-${err.error_id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Resolve
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab !== 'errors' && (<>
      {/* Filters */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white"
                data-testid="ticket-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_customer">Waiting</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="bg-card border-white/15">
        <CardContent className="p-0">
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">No tickets found</p>
              <Button variant="outline" className="border-white/20" onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Ticket
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/15 hover:bg-white/[0.07]">
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      className="border-white/20"
                    />
                  </TableHead>
                  <SortableTableHead sortKey="subject" currentSort={sort} onSort={setSort}>
                    Ticket
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="status" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                    currentFilter={statusFilter !== 'all' ? statusFilter : null}
                    onFilter={(val) => setStatusFilter(val || 'all')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="priority" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={Object.entries(PRIORITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                    currentFilter={priorityFilter !== 'all' ? priorityFilter : null}
                    onFilter={(val) => setPriorityFilter(val || 'all')}
                  >
                    Priority
                  </SortableTableHead>
                  <SortableTableHead sortKey="category" currentSort={sort} onSort={setSort}>
                    Category
                  </SortableTableHead>
                  <SortableTableHead sortKey="assigned_to_name" currentSort={sort} onSort={setSort}>
                    Assigned
                  </SortableTableHead>
                  <SortableTableHead sortKey="created_at" currentSort={sort} onSort={setSort}>
                    Created
                  </SortableTableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTickets.map((ticket) => {
                  const slaStatus = getSlaStatus(ticket);
                  return (
                  <TableRow
                    key={ticket.ticket_id}
                    className={`border-white/15 hover:bg-white/[0.07] cursor-pointer ${slaStatus?.type === 'breached' ? 'bg-red-500/5' : ''} ${selectedTickets.includes(ticket.ticket_id) ? 'bg-emerald-500/5' : ''}`}
                    data-testid={`ticket-row-${ticket.ticket_id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedTickets.includes(ticket.ticket_id)}
                        onCheckedChange={(checked) => handleSelectTicket(ticket.ticket_id, checked)}
                        className="border-white/20"
                      />
                    </TableCell>
                    <TableCell onClick={() => fetchTicketDetails(ticket.ticket_id)}>
                      <div className="flex items-start gap-2">
                        {slaStatus && (
                          <div className={`mt-1 ${slaStatus.type === 'breached' ? 'text-red-400' : 'text-yellow-400'}`} title={slaStatus.message}>
                            <Zap className="h-4 w-4" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white">{ticket.subject}</p>
                          <p className="text-xs text-muted-foreground">{ticket.ticket_id}</p>
                          {slaStatus && (
                            <p className={`text-xs ${slaStatus.type === 'breached' ? 'text-red-400' : 'text-yellow-400'}`}>
                              {slaStatus.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_CONFIG[ticket.status]?.color || 'bg-muted0/20 text-muted-foreground'}>
                        {STATUS_CONFIG[ticket.status]?.label || ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={PRIORITY_CONFIG[ticket.priority]?.color || 'bg-muted0/20 text-muted-foreground'}>
                        {PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`text-sm ${CATEGORY_CONFIG[ticket.category]?.color || 'text-muted-foreground'}`}>
                        {CATEGORY_CONFIG[ticket.category]?.label || ticket.category}
                      </span>
                    </TableCell>
                    <TableCell>
                      {ticket.assigned_to_name ? (
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-white">
                            {ticket.assigned_to_name.charAt(0)}
                          </div>
                          <span className="text-sm text-muted-foreground/80">{ticket.assigned_to_name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatTimeAgo(ticket.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="text-muted-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); fetchTicketDetails(ticket.ticket_id); }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAssignToMe(ticket.ticket_id); }}>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assign to Me
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(ticket.ticket_id, 'in_progress'); }}>
                            <Timer className="h-4 w-4 mr-2" />
                            Mark In Progress
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(ticket.ticket_id, 'resolved'); }}>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Mark Resolved
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Create Ticket Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-card border-white/15 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create Support Ticket</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Create a new support ticket for tracking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Subject *</label>
              <Input
                placeholder="Brief description of the issue"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Description *</label>
              <Textarea
                placeholder="Detailed description of the issue..."
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Priority</label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !newSubject.trim() || !newDescription.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Creating...' : 'Create Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, ticket: null })}>
        <DialogContent className="bg-card border-white/15 max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewDialog.ticket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-white text-lg">{viewDialog.ticket.subject}</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">{viewDialog.ticket.ticket_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={STATUS_CONFIG[viewDialog.ticket.status]?.color}>
                      {STATUS_CONFIG[viewDialog.ticket.status]?.label}
                    </Badge>
                    <Badge className={PRIORITY_CONFIG[viewDialog.ticket.priority]?.color}>
                      {PRIORITY_CONFIG[viewDialog.ticket.priority]?.label}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Ticket Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Tag className="h-4 w-4" />
                    <span>{CATEGORY_CONFIG[viewDialog.ticket.category]?.label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>{viewDialog.ticket.assigned_to_name || 'Unassigned'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Created {formatDate(viewDialog.ticket.created_at)}</span>
                  </div>
                  {viewDialog.ticket.customer_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      <span>{viewDialog.ticket.customer_name}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="p-4 rounded-lg bg-white/10 border border-white/20">
                  <p className="text-sm text-muted-foreground/80 whitespace-pre-wrap">{viewDialog.ticket.description}</p>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/20"
                    onClick={() => handleAssignToMe(viewDialog.ticket.ticket_id)}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Assign to Me
                  </Button>
                  <Select
                    value={viewDialog.ticket.status}
                    onValueChange={(value) => handleStatusChange(viewDialog.ticket.ticket_id, value)}
                  >
                    <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Comments */}
                <div>
                  <h4 className="text-white font-medium mb-3">Comments ({viewDialog.ticket.comments?.length || 0})</h4>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto">
                    {viewDialog.ticket.comments?.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No comments yet</p>
                    ) : (
                      viewDialog.ticket.comments?.map((comment) => (
                        <div
                          key={comment.comment_id}
                          className={`p-3 rounded-lg ${comment.internal ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-white/10'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full bg-white/15 flex items-center justify-center text-xs text-white">
                                {comment.author_name?.charAt(0) || '?'}
                              </div>
                              <span className="text-sm font-medium text-white">{comment.author_name}</span>
                              {comment.internal && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Internal</Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">{formatTimeAgo(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground/80">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Add Comment */}
                <div className="pt-4 border-t border-white/15">
                  <Textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="bg-white/10 border-white/20 text-white mb-3"
                    rows={2}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        className="rounded border-white/20"
                      />
                      Internal note (not visible to customer)
                    </label>
                    <Button
                      onClick={handleAddComment}
                      disabled={submitting || !newComment.trim()}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      {submitting ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templatesDialog} onOpenChange={setTemplatesDialog}>
        <DialogContent className="bg-card border-white/15 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Response Templates</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Manage predefined responses for common ticket scenarios
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Create new template */}
            <Card className="bg-white/[0.07] border-white/20">
              <CardContent className="pt-4">
                <h4 className="text-white font-medium mb-3">Create New Template</h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Template name (e.g., 'Password Reset Instructions')"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                  <Textarea
                    placeholder="Template content..."
                    value={newTemplateContent}
                    onChange={(e) => setNewTemplateContent(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                    rows={3}
                  />
                  <Button
                    onClick={async () => {
                      if (!newTemplateName.trim() || !newTemplateContent.trim()) {
                        toast.error('Please fill in both name and content');
                        return;
                      }
                      try {
                        const response = await fetch(
                          `${API_URL}/api/owner-admin/tickets/templates?name=${encodeURIComponent(newTemplateName)}&content=${encodeURIComponent(newTemplateContent)}`, { credentials: 'include', method: 'POST' }
                        );
                        if (response.ok) {
                          toast.success('Template created');
                          setNewTemplateName('');
                          setNewTemplateContent('');
                          fetchData();
                        }
                      } catch (error) {
                        toast.error('Failed to create template');
                      }
                    }}
                    disabled={!newTemplateName.trim() || !newTemplateContent.trim()}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Template
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing templates */}
            <div className="space-y-2">
              <h4 className="text-white font-medium">Saved Templates ({templates.length})</h4>
              {templates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No templates yet. Create your first one above.</p>
              ) : (
                templates.map((template) => (
                  <Card key={template.template_id} className="bg-white/[0.07] border-white/20">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="text-white font-medium">{template.name}</h5>
                          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{template.content}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setNewComment(template.content);
                              setTemplatesDialog(false);
                            }}
                            className="text-muted-foreground"
                          >
                            Use
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await fetch(
                                  `${API_URL}/api/owner-admin/tickets/templates/${template.template_id}`, { credentials: 'include', method: 'DELETE' }
                                );
                                toast.success('Template deleted');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to delete template');
                              }
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
